import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNative, isAndroid } from '../services/platform.js';
import { API_BASE_URL } from '../services/http.js';
import { isVersionBelow } from '../utils/semver.js';
import { useTheme } from './useTheme.jsx';
import { useAuth } from './useAuth.jsx';

// Chave de localStorage para lembrar qual versão de aviso "há atualização
// disponível" já foi dispensada — reaparece a cada versão nova, não a cada
// abertura do app.
const DISMISSED_UPDATE_KEY = 'cgpt.dismissedUpdateVersion';

// Cor da barra de status por tema. Precisa acompanhar o fundo real de cada
// tema (definidos em index.css), senão no tema claro a status bar fica preta
// sobre fundo claro.
const STATUS_BAR_BY_THEME = {
    dark: { background: '#05070A', style: 'DARK' },
    contemplativo: { background: '#1c1b1f', style: 'DARK' },
    light: { background: '#f3f4f6', style: 'LIGHT' },
};

// 'DARK'/'LIGHT' no plugin referem-se ao esquema do CONTEÚDO, não do texto:
// Style.Dark = fundo escuro, portanto texto claro. Nome confuso do plugin.

/**
 * Integra o app com o shell nativo. No-op completo na web.
 *
 * Monte UMA vez, dentro dos providers de tema e de auth.
 */
export function useNativeShell() {
    const { theme } = useTheme();
    const { refreshSession, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [versionGate, setVersionGate] = useState({ updateRequired: false, updateAvailable: false, storeUrl: '', latestVersion: '' });

    // --- Status bar acompanha o tema ---
    useEffect(() => {
        if (!isNative()) return;
        let cancelled = false;

        (async () => {
            try {
                const { StatusBar, Style } = await import('@capacitor/status-bar');
                if (cancelled) return;
                const conf = STATUS_BAR_BY_THEME[theme] ?? STATUS_BAR_BY_THEME.dark;
                await StatusBar.setStyle({ style: conf.style === 'LIGHT' ? Style.Light : Style.Dark });
                // setBackgroundColor só tem efeito no Android; no iOS a cor vem
                // do conteúdo sob a status bar. Chamar no iOS não quebra.
                await StatusBar.setBackgroundColor({ color: conf.background }).catch(() => {});
            } catch {
                // Plugin ausente: não é motivo para derrubar o app.
            }
        })();

        return () => { cancelled = true; };
    }, [theme]);

    // --- Teclado ---
    useEffect(() => {
        if (!isNative()) return;
        let handles = [];

        (async () => {
            try {
                const { Keyboard } = await import('@capacitor/keyboard');
                // Sem isto, no WKWebView a área de toque fica dessincronizada
                // da posição visual depois que o teclado fecha: botões abaixo
                // de onde o teclado cobria param de responder. O scroll força
                // o WebView a recalcular.
                handles.push(await Keyboard.addListener('keyboardDidHide', () => {
                    window.scrollTo(0, 0);
                }));

                // Altura do teclado exposta como custom property, para o CSS
                // reposicionar o input do chat sem precisar de estado React
                // (que re-renderizaria a lista de mensagens a cada frame).
                handles.push(await Keyboard.addListener('keyboardWillShow', (info) => {
                    document.documentElement.style.setProperty(
                        '--keyboard-height', `${info?.keyboardHeight ?? 0}px`
                    );
                    document.documentElement.classList.add('keyboard-open');
                }));
                handles.push(await Keyboard.addListener('keyboardWillHide', () => {
                    document.documentElement.style.setProperty('--keyboard-height', '0px');
                    document.documentElement.classList.remove('keyboard-open');
                }));
            } catch {
                // sem plugin de teclado, segue sem ajuste
            }
        })();

        return () => {
            handles.forEach((h) => h?.remove?.());
            handles = [];
            document.documentElement.classList.remove('keyboard-open');
        };
    }, []);

    // --- Botão físico "voltar" do Android ---
    useEffect(() => {
        if (!isNative() || !isAndroid()) return;
        let handle = null;
        let cancelled = false;

        (async () => {
            try {
                const { App } = await import('@capacitor/app');
                if (cancelled) return;
                handle = await App.addListener('backButton', ({ canGoBack }) => {
                    // Sem tratamento, o botão físico FECHA o app de qualquer
                    // tela — comportamento que o usuário lê como crash.
                    if (canGoBack && location.pathname !== '/chat' && location.pathname !== '/inicio') {
                        navigate(-1);
                    } else {
                        // Na raiz, minimizar é o comportamento esperado no
                        // Android; encerrar o app perderia o estado do chat.
                        App.minimizeApp();
                    }
                });
            } catch {
                // sem plugin, o comportamento padrão do sistema permanece
            }
        })();

        return () => {
            cancelled = true;
            handle?.remove?.();
        };
    }, [navigate, location.pathname]);

    // --- Volta do background ---
    useEffect(() => {
        if (!isNative()) return;
        let handle = null;
        let cancelled = false;

        (async () => {
            try {
                const { App } = await import('@capacitor/app');
                if (cancelled) return;
                handle = await App.addListener('appStateChange', ({ isActive }) => {
                    // O app pode ficar dias em background e o access token
                    // expira (validade definida em ACCESS_TOKEN_EXPIRE_MINUTES no
                    // backend). Sem revalidar, o usuário volta para
                    // uma tela que parece logada mas falha na primeira ação.
                    if (isActive && isAuthenticated) {
                        refreshSession();
                    }
                });
            } catch {
                // sem plugin, sem revalidação automática
            }
        })();

        return () => {
            cancelled = true;
            handle?.remove?.();
        };
    }, [isAuthenticated, refreshSession]);

    // --- Gate de versão mínima ---
    // Ao contrário da web (que redeploya e todo mundo recebe a correção),
    // usuários do app podem ficar presos numa versão antiga por semanas.
    // Consulta uma única vez no boot; não há necessidade de recheck em
    // background (o app não fica aberto por semanas contínuas).
    useEffect(() => {
        if (!isNative()) return;
        let cancelled = false;

        (async () => {
            try {
                const [{ App }, res] = await Promise.all([
                    import('@capacitor/app'),
                    fetch(`${API_BASE_URL}/public/app-version`),
                ]);
                if (cancelled || !res.ok) return;
                const info = await App.getInfo();
                const data = await res.json();
                const currentVersion = info?.version ?? '0.0.0';
                const isAndroidPlatform = isAndroid();
                const storeUrl = isAndroidPlatform ? data.store_url_android : data.store_url_ios;

                if (isVersionBelow(currentVersion, data.min_supported)) {
                    if (!cancelled) setVersionGate({ updateRequired: true, updateAvailable: false, storeUrl, latestVersion: data.latest });
                    return;
                }
                if (isVersionBelow(currentVersion, data.latest)) {
                    const dismissedVersion = window.localStorage.getItem(DISMISSED_UPDATE_KEY);
                    if (!cancelled && dismissedVersion !== data.latest) {
                        setVersionGate({ updateRequired: false, updateAvailable: true, storeUrl, latestVersion: data.latest });
                    }
                }
            } catch {
                // Falha ao checar versão (offline, endpoint fora do ar) não
                // deve travar o app — segue sem gate.
            }
        })();

        return () => { cancelled = true; };
    }, []);

    const dismissUpdateAvailable = () => {
        window.localStorage.setItem(DISMISSED_UPDATE_KEY, versionGate.latestVersion);
        setVersionGate((prev) => ({ ...prev, updateAvailable: false }));
    };

    return { ...versionGate, dismissUpdateAvailable };
}
